// Peptide checkout shipping methods — FE mirror of backend lib/peptideShipping.js.
//
// Money contract (spec: docs/superpowers/specs/2026-07-03-peptide-shipping-method-design.md):
// the client sends ONLY shippingMethodId; the backend resolves the fee
// server-side and persists order.shippingCents SEPARATE from the product-only
// order.totalCents. The FE uses this module purely for DISPLAY (picker options,
// fee shown in totals) — the charged amount always comes from the server.
//
// Method sources by persona:
//  - provider / patient / sales rep: the catalog Get response's
//    `shippingMethods` array (already resolved + normalized by the BE).
//  - manufacturer: the raw DoctorOffice `peptideShippingSettings` (their
//    catalog data comes from getOrgInfo, not the catalog process) — normalized
//    here with the same defaults the BE applies.
// `normalizeShippingMethods` accepts both shapes so hooks have one call site.

// Mirror of BE DEFAULT_PEPTIDE_SHIPPING_METHODS (lib/peptideShipping.js) —
// keep the two lists in sync or the manufacturer checkout will show different
// options than the fee the server resolves.
export const DEFAULT_PEPTIDE_SHIPPING_METHODS = [
  {
    id: "florida-overnight",
    label: "FLORIDA ONLY - Overnight or Cold Shipping",
    priceCents: 3500,
    estimatedDaysMin: 1,
    estimatedDaysMax: 1,
    statesAllowed: ["FL"],
  },
  {
    id: "standard",
    label: "Standard 3-4 Day Shipping",
    priceCents: 3500,
    estimatedDaysMin: 3,
    estimatedDaysMax: 4,
    statesAllowed: null,
  },
  {
    id: "overnight-cold",
    label: "Overnight or Cold Shipping",
    priceCents: 5000,
    estimatedDaysMin: 1,
    estimatedDaysMax: 1,
    statesAllowed: null,
  },
];

// Minimal full-name → USPS code map (mirror of the BE's) so a free-text state
// input ("Florida") still matches a state-gated method in the picker.
const STATE_NAME_TO_CODE = {
  ALABAMA: "AL",
  ALASKA: "AK",
  ARIZONA: "AZ",
  ARKANSAS: "AR",
  CALIFORNIA: "CA",
  COLORADO: "CO",
  CONNECTICUT: "CT",
  DELAWARE: "DE",
  FLORIDA: "FL",
  GEORGIA: "GA",
  HAWAII: "HI",
  IDAHO: "ID",
  ILLINOIS: "IL",
  INDIANA: "IN",
  IOWA: "IA",
  KANSAS: "KS",
  KENTUCKY: "KY",
  LOUISIANA: "LA",
  MAINE: "ME",
  MARYLAND: "MD",
  MASSACHUSETTS: "MA",
  MICHIGAN: "MI",
  MINNESOTA: "MN",
  MISSISSIPPI: "MS",
  MISSOURI: "MO",
  MONTANA: "MT",
  NEBRASKA: "NE",
  NEVADA: "NV",
  "NEW HAMPSHIRE": "NH",
  "NEW JERSEY": "NJ",
  "NEW MEXICO": "NM",
  "NEW YORK": "NY",
  "NORTH CAROLINA": "NC",
  "NORTH DAKOTA": "ND",
  OHIO: "OH",
  OKLAHOMA: "OK",
  OREGON: "OR",
  PENNSYLVANIA: "PA",
  "RHODE ISLAND": "RI",
  "SOUTH CAROLINA": "SC",
  "SOUTH DAKOTA": "SD",
  TENNESSEE: "TN",
  TEXAS: "TX",
  UTAH: "UT",
  VERMONT: "VT",
  VIRGINIA: "VA",
  WASHINGTON: "WA",
  "WEST VIRGINIA": "WV",
  WISCONSIN: "WI",
  WYOMING: "WY",
  "DISTRICT OF COLUMBIA": "DC",
  "PUERTO RICO": "PR",
};

export const normalizeStateCode = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim().toUpperCase();
  if (!s) return null;
  if (s.length === 2) return s;
  return STATE_NAME_TO_CODE[s] || s;
};

const sanitizeCents = (raw) => {
  const cents = parseInt(raw, 10);
  return Number.isFinite(cents) && cents >= 0 ? cents : null;
};

const sanitizeDays = (raw, fallback) => {
  const d = parseInt(raw, 10);
  return Number.isFinite(d) && d >= 0 ? d : fallback;
};

const normalizeMethod = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id != null ? String(raw.id).trim() : "";
  const label = raw.label != null ? String(raw.label).trim() : "";
  const priceCents = sanitizeCents(raw.priceCents);
  if (!id || !label || priceCents == null) return null;
  let statesAllowed = null;
  if (Array.isArray(raw.statesAllowed) && raw.statesAllowed.length > 0) {
    statesAllowed = raw.statesAllowed.map(normalizeStateCode).filter(Boolean);
    if (statesAllowed.length === 0) statesAllowed = null;
  }
  const daysMin = sanitizeDays(raw.estimatedDaysMin, 1);
  return {
    id,
    label,
    priceCents,
    estimatedDaysMin: daysMin,
    estimatedDaysMax: sanitizeDays(raw.estimatedDaysMax, daysMin),
    statesAllowed,
  };
};

// Accepts: a resolved methods ARRAY (catalog Get response — may arrive as a
// JSON string per the platform's array-output serialization quirk), OR a raw
// peptideShippingSettings OBJECT ({ methods: [...] }), OR null. Always returns
// a non-empty normalized list (defaults as the floor), matching the BE's
// "picker must never be empty because of a config typo" posture.
export const normalizeShippingMethods = (raw) => {
  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      value = null;
    }
  }
  const list = Array.isArray(value) ? value : value && Array.isArray(value.methods) ? value.methods : null;
  if (list) {
    const normalized = list.map(normalizeMethod).filter(Boolean);
    if (normalized.length > 0) return normalized;
  }
  return DEFAULT_PEPTIDE_SHIPPING_METHODS;
};

// ZIP↔state consistency (mirror of the BE gate): the typed State field is
// free text and trivially spoofable ("fl" on an Atlanta address), while USPS
// ZIP prefixes deterministically identify the state — the ZIP is the ground
// truth for state-gated methods. Per-state 3-digit prefix sets, maintained
// only for states that appear in a gate (FL today). Florida: 320xx–339xx
// plus 341/342/344/346/347/349 (340 is Armed Forces Americas; 343/345/348
// unassigned). Keep in sync with STATE_ZIP3 in the BE lib/peptideShipping.js.
const STATE_ZIP3 = {
  FL: new Set([
    ...Array.from({ length: 20 }, (_, i) => String(320 + i)), // 320–339
    "341",
    "342",
    "344",
    "346",
    "347",
    "349",
  ]),
};

// First 3 digits of the 5-digit ZIP core ("33101", "33101-1234"); null when
// there is no valid 5-digit core.
export const zip3 = (raw) => {
  if (raw == null) return null;
  const m = String(raw)
    .trim()
    .match(/^(\d{5})(?:-\d{4})?$/);
  return m ? m[1].slice(0, 3) : null;
};

// True when the address plausibly sits in `stateCode` for gating purposes:
// state field matches AND, when we hold ZIP data for that state, the ZIP
// corroborates it. Missing/malformed ZIPs fail closed for verifiable states —
// a gated method needs proof (same rule as the server).
export const addressQualifiesForState = (stateCode, state, zip) => {
  if (normalizeStateCode(state) !== stateCode) return false;
  const zipSet = STATE_ZIP3[stateCode];
  if (!zipSet) return true; // no ZIP data for this state — state-field check only
  const prefix = zip3(zip);
  return prefix != null && zipSet.has(prefix);
};

// Methods selectable for a given shipping address. A state-gated method
// (e.g. FLORIDA ONLY) is hidden until the entered state AND ZIP both prove
// the gated state; methods with no gate always show. Mirrors the BE, which
// rejects a gated method whose address doesn't qualify.
export const filterShippingMethodsByState = (methods, state, zip) => {
  const list = Array.isArray(methods) ? methods : [];
  return list.filter((m) => !m.statesAllowed || m.statesAllowed.some((allowed) => addressQualifiesForState(allowed, state, zip)));
};

export const getShippingMethodById = (methods, id) => {
  if (!id) return null;
  return (Array.isArray(methods) ? methods : []).find((m) => m.id === id) || null;
};

// Skip weekends — mirrors checkout/steps/ReviewStep.js's delivery-window math
// so the picker and the review step can't disagree about dates.
const addBusinessDays = (from, days) => {
  const d = new Date(from);
  let remaining = days;
  while (remaining > 0) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) remaining -= 1;
  }
  return d;
};

// Intl.DateTimeFormat construction is the expensive part of date formatting
// (locale-data lookup); the picker calls estimatedDeliveryLabel once per
// option per render, so cache one formatter per language for the app's life.
const deliveryDateFormatters = new Map();
const getDeliveryDateFormatter = (lang) => {
  if (!deliveryDateFormatters.has(lang)) {
    deliveryDateFormatters.set(lang, new Intl.DateTimeFormat(lang, { weekday: "short", month: "short", day: "numeric" }));
  }
  return deliveryDateFormatters.get(lang);
};

// "Tue, Jul 7" (single day) or "Tue, Jul 7 – Thu, Jul 9" (window), localized.
export const estimatedDeliveryLabel = (method, lang = "en") => {
  if (!method) return "";
  const fmt = getDeliveryDateFormatter(lang);
  const now = new Date();
  const first = fmt.format(addBusinessDays(now, method.estimatedDaysMin));
  if (method.estimatedDaysMax > method.estimatedDaysMin) {
    return `${first} – ${fmt.format(addBusinessDays(now, method.estimatedDaysMax))}`;
  }
  return first;
};
